// Automatic messages: the condition catalogue and the matcher. The loader runs
// `matches()` on visitors' pages and the builder runs it in its "Test URL" box,
// so what admins preview is what visitors get.
import catalogue from './trigger-conditions.json';

export type ConditionType = keyof typeof catalogue;
export type TriggerStart = 'page_view' | 'first_visit';
export type TriggerFrequency = 'once_per_visitor' | 'once_per_session' | 'every_time';

export interface ConditionSpec {
    operators: string[];
    /** The rule names something (a URL parameter, a custom attribute). */
    key: boolean;
    /** 'text' | 'number', a list of options, or null when the operator says it all. */
    value: 'text' | 'number' | string[] | null;
}

export const CONDITIONS = catalogue as Record<ConditionType, ConditionSpec>;

/** Operators that only test presence and take no value. */
export const VALUELESS_OPERATORS = ['exists', 'not_exists'];

export interface TriggerRule {
    type: ConditionType;
    operator: string;
    key?: string;
    value?: string | number;
}

/** A "Trigger" node in the builder: a start event and rules that must all hold. */
export interface TriggerGroup {
    event: TriggerStart;
    rules: TriggerRule[];
}

/** The message fires when any group matches. */
export interface TriggerConditions {
    groups: TriggerGroup[];
}

/** A trigger as the loader receives it at boot. */
export interface VisitorTrigger {
    id: string;
    conditions: TriggerConditions;
    delay_seconds: number;
    frequency: TriggerFrequency;
    message: string;
    /** Null: sent as the inbox (workspace name and brand colour). */
    sender: { name: string; avatar_url: string | null } | null;
}

/** Facts about the visitor only the server knows, sent at boot. */
export interface TriggerBootContext {
    returning: boolean;
    open_conversation: boolean;
    attributes: Record<string, unknown>;
}

export interface MatchContext {
    url: string;
    referrer: string;
    device: 'desktop' | 'mobile';
    secondsOnSite: number;
    returning: boolean;
    identified: boolean;
    emailKnown: boolean;
    attributes: Record<string, unknown>;
    /** Null when the inbox has no office hours (always open). */
    officeHoursOpen: boolean | null;
}

export function needsValue(rule: Pick<TriggerRule, 'type' | 'operator'>): boolean {
    return CONDITIONS[rule.type].value !== null && !VALUELESS_OPERATORS.includes(rule.operator);
}

/** `*` matches anything; everything else is literal. Case-insensitive. */
export function wildcard(pattern: string): RegExp {
    const escaped = pattern
        .split('*')
        .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
        .join('.*');

    return new RegExp(`^${escaped}$`, 'i');
}

function parseUrl(url: string): URL | null {
    try {
        return new URL(url);
    } catch {
        return null;
    }
}

function trimSlash(value: string): string {
    return value.length > 1 ? value.replace(/\/+(?=\?|$)/, '') : value;
}

/**
 * What a page URL rule compares against: the full address when the rule names
 * a site ("https://…"), otherwise the path, so "/pricing" works on every
 * domain the messenger is installed on. The query string only counts when the
 * rule has one: "/pricing" also matches "/pricing?utm_source=ads".
 */
function urlTarget(value: string, url: URL): string {
    const query = value.includes('?') ? url.search : '';
    return value.includes('://') ? `${url.origin}${url.pathname}${query}` : `${url.pathname}${query}`;
}

function compareText(operator: string, actual: string | null, expected: string): boolean {
    const a = (actual ?? '').toLowerCase();
    const e = expected.toLowerCase();

    switch (operator) {
        case 'exists':
            return actual !== null && actual !== '';
        case 'not_exists':
            return actual === null || actual === '';
        case 'equals':
            return actual !== null && a === e;
        case 'not_equals':
            return a !== e;
        case 'contains':
            return actual !== null && a.includes(e);
        case 'not_contains':
            return !a.includes(e);
        case 'starts_with':
            return actual !== null && a.startsWith(e);
        default:
            return false;
    }
}

function matchesPage(rule: TriggerRule, href: string): boolean {
    const url = parseUrl(href);
    const value = String(rule.value ?? '').trim();

    if (!url) return false;

    switch (rule.operator) {
        case 'contains':
        case 'not_contains':
            return compareText(rule.operator, `${url.origin}${url.pathname}${url.search}`, value);
        case 'equals':
        case 'not_equals':
            return compareText(rule.operator, trimSlash(urlTarget(value, url)), trimSlash(value));
        case 'starts_with':
            return compareText(rule.operator, urlTarget(value, url), value);
        case 'matches':
            return (
                wildcard(value).test(urlTarget(value, url)) || wildcard(value).test(trimSlash(urlTarget(value, url)))
            );
        default:
            return false;
    }
}

function matchesAttribute(rule: TriggerRule, attributes: Record<string, unknown>): boolean {
    const raw = attributes[rule.key ?? ''];
    const actual = raw === undefined || raw === null ? null : String(raw);

    if (rule.operator === 'gt' || rule.operator === 'lt') {
        const a = Number(raw);
        const e = Number(rule.value);
        if (actual === null || Number.isNaN(a) || Number.isNaN(e)) return false;

        return rule.operator === 'gt' ? a > e : a < e;
    }

    return compareText(rule.operator, actual, String(rule.value ?? ''));
}

export function matchesRule(rule: TriggerRule, ctx: MatchContext): boolean {
    switch (rule.type) {
        case 'page_url':
            return matchesPage(rule, ctx.url);
        case 'url_param': {
            const url = parseUrl(ctx.url);
            return compareText(rule.operator, url?.searchParams.get(rule.key ?? '') ?? null, String(rule.value ?? ''));
        }
        case 'referrer':
            return compareText(rule.operator, ctx.referrer || null, String(rule.value ?? ''));
        case 'time_on_site':
            return ctx.secondsOnSite >= Number(rule.value ?? 0);
        case 'visitor_type':
            return (rule.value === 'returning') === ctx.returning;
        case 'identified':
            return (rule.operator === 'is') === ctx.identified;
        case 'email_known':
            return (rule.operator === 'is') === ctx.emailKnown;
        case 'attribute':
            return matchesAttribute(rule, ctx.attributes);
        case 'office_hours':
            return (rule.value === 'open') === (ctx.officeHoursOpen !== false);
        case 'device':
            return rule.value === ctx.device;
        default:
            // A condition this loader doesn't know (newer server): never match.
            return false;
    }
}

/**
 * Whether one start group applies to this visitor and page. With `ignoreTime`,
 * time-on-site rules are left for `waitMs()` to schedule.
 */
export function matchesGroup(group: TriggerGroup, ctx: MatchContext, { ignoreTime = false } = {}): boolean {
    if (group.event === 'first_visit' && ctx.returning) return false;

    return group.rules.every((rule) => (ignoreTime && rule.type === 'time_on_site') || matchesRule(rule, ctx));
}

/** The first start group that applies, if any. */
export function matchingGroup(
    trigger: Pick<VisitorTrigger, 'conditions'>,
    ctx: MatchContext,
    options: { ignoreTime?: boolean } = {},
): TriggerGroup | undefined {
    return trigger.conditions.groups.find((group) => matchesGroup(group, ctx, options));
}

export function matches(
    trigger: Pick<VisitorTrigger, 'conditions'>,
    ctx: MatchContext,
    options: { ignoreTime?: boolean } = {},
): boolean {
    return matchingGroup(trigger, ctx, options) !== undefined;
}

/**
 * How long to wait before showing the trigger: its delay, or until the
 * visitor's been on the site as long as the matching group needs.
 */
export function waitMs(trigger: Pick<VisitorTrigger, 'delay_seconds'>, group: TriggerGroup, ctx: MatchContext): number {
    const siteWait = group.rules
        .filter((rule) => rule.type === 'time_on_site')
        .map((rule) => Number(rule.value ?? 0) - ctx.secondsOnSite);

    return Math.max(0, trigger.delay_seconds, ...siteWait) * 1000;
}

const OPERATOR_LABELS: Record<string, string> = {
    equals: 'is',
    not_equals: 'is not',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    matches: 'matches pattern',
    exists: 'is present',
    not_exists: 'is missing',
    gte: 'at least',
    gt: 'is greater than',
    lt: 'is less than',
    is: 'is',
    is_not: 'is not',
};

export const CONDITION_LABELS: Record<ConditionType, string> = {
    page_url: 'Page URL',
    url_param: 'URL parameter',
    referrer: 'Referrer',
    time_on_site: 'Time on site',
    visitor_type: 'Visitor',
    identified: 'Signed in',
    email_known: 'Email known',
    attribute: 'Custom attribute',
    office_hours: 'Office hours',
    device: 'Device',
};

/** The order conditions are offered in the builder. */
export const CONDITION_ORDER: ConditionType[] = [
    'page_url',
    'url_param',
    'referrer',
    'time_on_site',
    'visitor_type',
    'identified',
    'email_known',
    'attribute',
    'office_hours',
    'device',
];

const VALUE_LABELS: Record<string, string> = {
    new: 'new',
    returning: 'returning',
    open: 'open',
    closed: 'closed',
    desktop: 'desktop',
    mobile: 'mobile',
};

export function operatorLabel(type: ConditionType, operator: string): string {
    if (type === 'identified' || type === 'email_known') return operator === 'is' ? 'yes' : 'no';

    return OPERATOR_LABELS[operator] ?? operator;
}

export function valueLabel(value: string): string {
    return VALUE_LABELS[value] ?? value;
}

/** "Page URL contains /pricing" — for trigger list summaries. */
export function describeRule(rule: TriggerRule): string {
    const subject = rule.key ? `${CONDITION_LABELS[rule.type]} “${rule.key}”` : CONDITION_LABELS[rule.type];
    const value = needsValue(rule)
        ? ` ${rule.type === 'time_on_site' ? `${rule.value}s` : valueLabel(String(rule.value ?? ''))}`
        : '';

    return `${subject} ${operatorLabel(rule.type, rule.operator)}${value}`;
}

/** A blank rule of the given type, with its first operator and option. */
export function blankRule(type: ConditionType): TriggerRule {
    const spec = CONDITIONS[type];
    const rule: TriggerRule = { type, operator: spec.operators[0] };
    if (spec.key) rule.key = '';
    if (Array.isArray(spec.value)) rule.value = spec.value[0];
    else if (spec.value === 'number') rule.value = 30;
    else if (spec.value === 'text') rule.value = '';

    return rule;
}
